<template>
  <div class="echarts-demo-page">
    <h1 class="page-title">ECharts 实战笔记可视化 Demo</h1>
    
    <!-- 基础配置通用版 -->
    <div class="chart-card">
      <h2 class="card-title">1. 基础配置通用版（工具箱 + 自定义 tooltip + 多 Y 轴）</h2>
      <div class="card-content">
        <div class="chart-left">
          <div ref="baseChart" class="chart-container"></div>
        </div>
        <div class="chart-right">
          <h3 class="section-title">特殊属性配置</h3>
          <div class="prop-list">
            <div class="prop-item" v-for="(prop, index) in baseProps" :key="index">
              <code class="prop-name">{{ prop.name }}</code>
              <p class="prop-desc">{{ prop.desc }}</p>
              <button class="copy-btn" @click="copyCode(prop.code)">复制</button>
            </div>
          </div>
        </div>
      </div>
      <div class="card-footer">
        <button class="toggle-btn" @click="toggleCode('base')">
          {{ showCode.base ? '收起代码' : '展开代码' }}
        </button>
      </div>
      <div class="code-panel" v-show="showCode.base">
        <pre class="code-block"><code>{{ baseOptionCode }}</code></pre>
        <button class="copy-btn full" @click="copyCode(baseOptionCode)">复制完整配置</button>
      </div>
    </div>

    <!-- 饼状图 -->
    <div class="chart-card">
      <h2 class="card-title">2. 饼状图（自定义位置 + 间隙 + tooltip）</h2>
      <div class="card-content">
        <div class="chart-left">
          <div ref="pieChart" class="chart-container"></div>
        </div>
        <div class="chart-right">
          <h3 class="section-title">特殊属性配置</h3>
          <div class="prop-list">
            <div class="prop-item" v-for="(prop, index) in pieProps" :key="index">
              <code class="prop-name">{{ prop.name }}</code>
              <p class="prop-desc">{{ prop.desc }}</p>
              <button class="copy-btn" @click="copyCode(prop.code)">复制</button>
            </div>
          </div>
        </div>
      </div>
      <div class="card-footer">
        <button class="toggle-btn" @click="toggleCode('pie')">
          {{ showCode.pie ? '收起代码' : '展开代码' }}
        </button>
      </div>
      <div class="code-panel" v-show="showCode.pie">
        <pre class="code-block"><code>{{ pieOptionCode }}</code></pre>
        <button class="copy-btn full" @click="copyCode(pieOptionCode)">复制完整配置</button>
      </div>
    </div>

    <!-- 柱状图（含自动高亮） -->
    <div class="chart-card">
      <h2 class="card-title">3. 柱状图（自动循环高亮 + X 轴滚动条）</h2>
      <div class="card-content">
        <div class="chart-left">
          <div ref="barChart" class="chart-container"></div>
        </div>
        <div class="chart-right">
          <h3 class="section-title">特殊属性配置</h3>
          <div class="prop-list">
            <div class="prop-item" v-for="(prop, index) in barProps" :key="index">
              <code class="prop-name">{{ prop.name }}</code>
              <p class="prop-desc">{{ prop.desc }}</p>
              <button class="copy-btn" @click="copyCode(prop.code)">复制</button>
            </div>
          </div>
        </div>
      </div>
      <div class="card-footer">
        <button class="toggle-btn" @click="toggleCode('bar')">
          {{ showCode.bar ? '收起代码' : '展开代码' }}
        </button>
        <button class="action-btn" @click="toggleHighlight">
          {{ highlightTimer ? '停止高亮' : '开始高亮' }}
        </button>
      </div>
      <div class="code-panel" v-show="showCode.bar">
        <pre class="code-block"><code>{{ barOptionCode }}</code></pre>
        <button class="copy-btn full" @click="copyCode(barOptionCode)">复制完整配置</button>
      </div>
    </div>

    <!-- 折线图 -->
    <div class="chart-card">
      <h2 class="card-title">4. 折线图（symbol 样式 + 阴影 + 渐变区域）</h2>
      <div class="card-content">
        <div class="chart-left">
          <div ref="lineChart" class="chart-container"></div>
        </div>
        <div class="chart-right">
          <h3 class="section-title">特殊属性配置</h3>
          <div class="prop-list">
            <div class="prop-item" v-for="(prop, index) in lineProps" :key="index">
              <code class="prop-name">{{ prop.name }}</code>
              <p class="prop-desc">{{ prop.desc }}</p>
              <button class="copy-btn" @click="copyCode(prop.code)">复制</button>
            </div>
          </div>
        </div>
      </div>
      <div class="card-footer">
        <button class="toggle-btn" @click="toggleCode('line')">
          {{ showCode.line ? '收起代码' : '展开代码' }}
        </button>
      </div>
      <div class="code-panel" v-show="showCode.line">
        <pre class="code-block"><code>{{ lineOptionCode }}</code></pre>
        <button class="copy-btn full" @click="copyCode(lineOptionCode)">复制完整配置</button>
      </div>
    </div>

    <!-- 两柱一线组合图 -->
    <div class="chart-card">
      <h2 class="card-title">5. 两柱一线组合图（双 Y 轴：件 / %）</h2>
      <div class="card-content">
        <div class="chart-left">
          <div ref="mixChart" class="chart-container"></div>
        </div>
        <div class="chart-right">
          <h3 class="section-title">特殊属性配置</h3>
          <div class="prop-list">
            <div class="prop-item" v-for="(prop, index) in mixProps" :key="index">
              <code class="prop-name">{{ prop.name }}</code>
              <p class="prop-desc">{{ prop.desc }}</p>
              <button class="copy-btn" @click="copyCode(prop.code)">复制</button>
            </div>
          </div>
        </div>
      </div>
      <div class="card-footer">
        <button class="toggle-btn" @click="toggleCode('mix')">
          {{ showCode.mix ? '收起代码' : '展开代码' }}
        </button>
      </div>
      <div class="code-panel" v-show="showCode.mix">
        <pre class="code-block"><code>{{ mixOptionCode }}</code></pre>
        <button class="copy-btn full" @click="copyCode(mixOptionCode)">复制完整配置</button>
      </div>
    </div>

    <!-- 一项三柱柱状图 -->
    <div class="chart-card">
      <h2 class="card-title">6. 一项三柱柱状图（多系列 + X 轴滚动条）</h2>
      <div class="card-content">
        <div class="chart-left">
          <div ref="multiBarChart" class="chart-container"></div>
        </div>
        <div class="chart-right">
          <h3 class="section-title">特殊属性配置</h3>
          <div class="prop-list">
            <div class="prop-item" v-for="(prop, index) in multiBarProps" :key="index">
              <code class="prop-name">{{ prop.name }}</code>
              <p class="prop-desc">{{ prop.desc }}</p>
              <button class="copy-btn" @click="copyCode(prop.code)">复制</button>
            </div>
          </div>
        </div>
      </div>
      <div class="card-footer">
        <button class="toggle-btn" @click="toggleCode('multiBar')">
          {{ showCode.multiBar ? '收起代码' : '展开代码' }}
        </button>
      </div>
      <div class="code-panel" v-show="showCode.multiBar">
        <pre class="code-block"><code>{{ multiBarOptionCode }}</code></pre>
        <button class="copy-btn full" @click="copyCode(multiBarOptionCode)">复制完整配置</button>
      </div>
    </div>

    <!-- 复制成功提示 -->
    <div class="toast" v-show="toast.show">{{ toast.message }}</div>
  </div>
</template>

<script>
import * as echarts from 'echarts'

export default {
  name: 'EchartsDemo',
  data() {
    return {
      // 图表实例
      charts: {},
      // 高亮定时器
      highlightTimer: null,
      highlightIndex: 0,
      // 代码展开状态
      showCode: {
        base: false,
        pie: false,
        bar: false,
        line: false,
        mix: false,
        multiBar: false
      },
      // 提示信息
      toast: {
        show: false,
        message: ''
      },
      // ========== 基础配置通用版属性 ==========
      baseProps: [
        {
          name: 'toolbox',
          desc: '右上角工具箱：dataView/magicType/restore/saveAsImage',
          code: `toolbox: {
  feature: {
    dataView: { show: true, readOnly: false },
    magicType: { show: true, type: ['line', 'bar'] },
    restore: { show: true },
    saveAsImage: { show: true }
  }
}`
        },
        {
          name: 'grid',
          desc: '整个图表偏移程度：left/right/top/bottom',
          code: `grid: {
  left: "2%",
  right: "5%",
  top: "22%",
  bottom: "12%",
  containLabel: true
}`
        },
        {
          name: 'tooltip.formatter',
          desc: '自定义悬浮框内容，使用 params.marker 生成小圆球',
          code: `tooltip: {
  formatter: (params) => {
    let html = \`<div>\${params[0].axisValue}</div>\`;
    params.forEach(item => {
      html += \`<div>\${item.marker}\${item.seriesName}:\${item.value}</div>\`;
    });
    return html;
  }
}`
        },
        {
          name: 'axisLabel.rotate',
          desc: 'X 轴标签倾斜角度（范围-90到90）',
          code: `axisLabel: {
  interval: 0,
  rotate: 38
}`
        },
        {
          name: 'yAxisIndex',
          desc: '多 Y 轴配置：在 series 中指定对应 Y 轴下标',
          code: `// yAxis 配置多个轴
yAxis: [
  { type: "value", name: "件" },
  { type: "value", name: "%", position: 'right' }
]
// series 中指定
{ name: '结案率', type: 'line', yAxisIndex: 1 }`
        }
      ],
      // ========== 饼状图属性 ==========
      pieProps: [
        {
          name: 'center',
          desc: '饼图中心位置：左右、上下百分比',
          code: `series: [{
  type: 'pie',
  center: ['40%', '50%'] // 左右、上下
}]`
        },
        {
          name: 'padAngle',
          desc: '每个饼块之间的间隙',
          code: `series: [{
  type: 'pie',
  padAngle: 2 // 间隙大小
}]`
        },
        {
          name: 'legend.right',
          desc: '右侧垂直居中图例',
          code: `legend: {
  left: 'right',
  top: 'middle',
  orient: 'vertical'
}`
        },
        {
          name: 'tooltip.formatter',
          desc: '自定义图例显示名称 + 数值',
          code: `tooltip: {
  trigger: 'item',
  formatter: function (params) {
    return params.name + ':' + params.value + '%';
  }
}`
        }
      ],
      // ========== 柱状图属性 ==========
      barProps: [
        {
          name: 'barWidth',
          desc: '柱子的宽度设置',
          code: `series: [{
  type: 'bar',
  barWidth: 20
}]`
        },
        {
          name: 'stack',
          desc: '柱状图堆叠配置',
          code: `series: [
  { name: 'A', type: 'bar', stack: "group1" },
  { name: 'B', type: 'bar', stack: "group1" }
]`
        },
        {
          name: 'dataZoom',
          desc: 'X 轴滚动条配置',
          code: `dataZoom: [{
  type: 'slider',
  xAxisIndex: [0],
  start: 0,
  end: 50
}]`
        },
        {
          name: 'dispatchAction',
          desc: '自动循环高亮柱子',
          code: `// 自动循环高亮
let index = 0;
setInterval(() => {
  chart.dispatchAction({
    type: 'showTip',
    seriesIndex: 0,
    dataIndex: index
  });
  index = (index + 1) % dataLength;
}, 3000);`
        }
      ],
      // ========== 折线图属性 ==========
      lineProps: [
        {
          name: 'symbolSize',
          desc: '数据点圆点大小',
          code: `series: [{
  type: 'line',
  symbolSize: 8
}]`
        },
        {
          name: 'lineStyle.shadow',
          desc: '折线阴影效果',
          code: `lineStyle: {
  shadowBlur: 3,
  shadowColor: 'rgba(7, 209, 250, 0.5)',
  shadowOffsetY: 8
}`
        },
        {
          name: 'itemStyle',
          desc: '数据点样式设置',
          code: `itemStyle: {
  color: '#fff',
  borderColor: '#fff',
  borderWidth: 2
}`
        },
        {
          name: 'areaStyle',
          desc: '区域渐变填充',
          code: `areaStyle: {
  color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
    { offset: 0, color: 'rgba(7, 209, 250, 0.3)' },
    { offset: 1, color: 'rgba(7, 209, 250, 0.1)' }
  ])
}`
        }
      ],
      // ========== 两柱一线属性 ==========
      mixProps: [
        {
          name: '双 Y 轴',
          desc: '左轴显示"件"，右轴显示"%"',
          code: `yAxis: [
  { type: 'value', name: '件', min: 0, max: 250 },
  { type: 'value', name: '%', min: 0, max: 100, position: 'right' }
]`
        },
        {
          name: 'yAxisIndex',
          desc: '指定折线使用第二个 Y 轴',
          code: `{
  name: '结案率',
  type: 'line',
  yAxisIndex: 1  // 使用右侧 Y 轴
}`
        },
        {
          name: 'tooltip.valueFormatter',
          desc: '格式化提示框数值',
          code: `tooltip: {
  valueFormatter: function (value) {
    return value + ' 件';
  }
}`
        }
      ],
      // ========== 一项三柱属性 ==========
      multiBarProps: [
        {
          name: '多 series',
          desc: '同一 X 轴下多个柱状图系列',
          code: `series: [
  { name: '案件数', type: 'bar' },
  { name: '处置中', type: 'bar' },
  { name: '已处置', type: 'bar' },
  { name: '超时', type: 'bar' }
]`
        },
        {
          name: 'dataZoom',
          desc: 'X 轴滚动条，控制显示范围',
          code: `dataZoom: [{
  type: 'slider',
  xAxisIndex: [0],
  start: 0,
  end: 50  // 只显示50%的数据
}]`
        },
        {
          name: 'axisLabel.interval',
          desc: '显示所有坐标点标签',
          code: `axisLabel: {
  interval: 0  // 0 表示显示所有标签
}`
        }
      ],
      // ========== 完整配置代码 ==========
      baseOptionCode: '',
      pieOptionCode: '',
      barOptionCode: '',
      lineOptionCode: '',
      mixOptionCode: '',
      multiBarOptionCode: ''
    }
  },
  mounted() {
    this.$nextTick(() => {
      this.initAllCharts()
      window.addEventListener('resize', this.handleResize)
    })
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.handleResize)
    this.clearHighlight()
    Object.values(this.charts).forEach(chart => chart.dispose())
  },
  methods: {
    // 初始化所有图表
    initAllCharts() {
      this.initBaseChart()
      this.initPieChart()
      this.initBarChart()
      this.initLineChart()
      this.initMixChart()
      this.initMultiBarChart()
    },
    
    // ========== 1. 基础配置通用版 ==========
    initBaseChart() {
      const chart = echarts.init(this.$refs.baseChart)
      const option = {
        title: { text: '基础配置示例', left: 'center', textStyle: { fontSize: 14 } },
        toolbox: {
          feature: {
            dataView: { show: true, readOnly: false },
            magicType: { show: true, type: ['line', 'bar'] },
            restore: { show: true },
            saveAsImage: { show: true }
          }
        },
        grid: {
          left: '10%',
          right: '15%',
          top: '20%',
          bottom: '15%'
        },
        tooltip: {
          trigger: 'axis',
          confine: true,
          formatter: function(params) {
            let str = params[0].axisValue + '<br/>'
            params.forEach(item => {
              str += `<span style="display:inline-block;margin-right:5px;border-radius:10px;width:9px;height:9px;background-color:${item.color};"></span>`
              str += item.seriesName + '：' + item.value + '<br/>'
            })
            return str
          }
        },
        legend: { data: ['在线人数', '离线人数', '在线率'], top: 30 },
        xAxis: {
          type: 'category',
          data: ['周一', '周二', '周三', '周四', '周五', '周六', '周日'],
          axisLabel: { interval: 0, rotate: 30 }
        },
        yAxis: [
          { type: 'value', name: '人', position: 'left' },
          { type: 'value', name: '%', position: 'right', min: 0, max: 100 }
        ],
        series: [
          { name: '在线人数', type: 'bar', data: [120, 132, 101, 134, 90, 230, 210], itemStyle: { color: '#5470c6' } },
          { name: '离线人数', type: 'bar', data: [20, 32, 11, 34, 10, 30, 21], itemStyle: { color: '#91cc75' } },
          { name: '在线率', type: 'line', yAxisIndex: 1, data: [85, 80, 90, 79, 90, 88, 90], itemStyle: { color: '#fac858' } }
        ]
      }
      chart.setOption(option)
      this.charts.base = chart
      this.baseOptionCode = JSON.stringify(option, null, 2)
    },
    
    // ========== 2. 饼状图 ==========
    initPieChart() {
      const chart = echarts.init(this.$refs.pieChart)
      const data = [
        { value: 40, name: '直接访问' },
        { value: 30, name: '邮件营销' },
        { value: 20, name: '联盟广告' },
        { value: 10, name: '视频广告' }
      ]
      const option = {
        title: { text: '饼状图示例', left: 'center', textStyle: { fontSize: 14 } },
        tooltip: {
          trigger: 'item',
          formatter: function(params) {
            return params.name + ': ' + params.value + '%'
          }
        },
        legend: {
          left: 'right',
          top: 'middle',
          orient: 'vertical',
          formatter: function(name) {
            const item = data.find(d => d.name === name)
            return name + '  ' + item.value + '%'
          },
          textStyle: { color: '#333', fontSize: 12 }
        },
        series: [{
          type: 'pie',
          center: ['40%', '50%'],
          padAngle: 2,
          radius: ['40%', '70%'],
          avoidLabelOverlap: false,
          itemStyle: {
            borderRadius: 5,
            borderColor: '#fff',
            borderWidth: 2
          },
          label: { show: false },
          emphasis: {
            label: { show: true, fontSize: 16, fontWeight: 'bold' }
          },
          data: data
        }]
      }
      chart.setOption(option)
      this.charts.pie = chart
      this.pieOptionCode = JSON.stringify(option, null, 2)
    },
    
    // ========== 3. 柱状图（含自动高亮） ==========
    initBarChart() {
      const chart = echarts.init(this.$refs.barChart)
      const xData = ['1月', '2月', '3月', '4月', '5月', '6月', '7月', '8月', '9月', '10月', '11月', '12月']
      const option = {
        title: { text: '柱状图 + 自动高亮', left: 'center', textStyle: { fontSize: 14 } },
        tooltip: {
          trigger: 'axis',
          axisPointer: { type: 'shadow' }
        },
        dataZoom: [{
          type: 'slider',
          xAxisIndex: [0],
          left: '10%',
          bottom: 0,
          start: 0,
          end: 50
        }],
        grid: { left: '10%', right: '10%', bottom: '15%', top: '15%' },
        xAxis: {
          type: 'category',
          data: xData,
          axisLabel: { interval: 0, rotate: 30 }
        },
        yAxis: { type: 'value', name: '件' },
        series: [
          {
            name: '处理事件数',
            type: 'bar',
            stack: 'A',
            barWidth: 20,
            color: 'rgba(19, 149, 95, 1)',
            data: [120, 132, 101, 134, 90, 230, 210, 180, 150, 200, 170, 190]
          },
          {
            name: '未处理数',
            type: 'bar',
            stack: 'A',
            barWidth: 20,
            color: 'rgba(237, 125, 49, 1)',
            data: [20, 32, 11, 34, 10, 30, 21, 25, 15, 20, 18, 22]
          }
        ]
      }
      chart.setOption(option)
      this.charts.bar = chart
      this.barOptionCode = JSON.stringify(option, null, 2)
      
      // 自动开始高亮
      this.startHighlight()
    },
    
    // 开始自动高亮
    startHighlight() {
      if (this.highlightTimer) return
      const chart = this.charts.bar
      const dataLength = 12
      this.highlightTimer = setInterval(() => {
        chart.dispatchAction({
          type: 'showTip',
          seriesIndex: 0,
          dataIndex: this.highlightIndex
        })
        chart.dispatchAction({
          type: 'highlight',
          seriesIndex: 0,
          dataIndex: this.highlightIndex
        })
        // 取消上一个高亮
        const prevIndex = this.highlightIndex === 0 ? dataLength - 1 : this.highlightIndex - 1
        chart.dispatchAction({
          type: 'downplay',
          seriesIndex: 0,
          dataIndex: prevIndex
        })
        this.highlightIndex = (this.highlightIndex + 1) % dataLength
      }, 2000)
    },
    
    // 停止自动高亮
    stopHighlight() {
      if (this.highlightTimer) {
        clearInterval(this.highlightTimer)
        this.highlightTimer = null
        // 取消所有高亮
        if (this.charts.bar) {
          this.charts.bar.dispatchAction({ type: 'downplay' })
          this.charts.bar.dispatchAction({ type: 'hideTip' })
        }
      }
    },
    
    // 切换高亮状态
    toggleHighlight() {
      if (this.highlightTimer) {
        this.stopHighlight()
      } else {
        this.startHighlight()
      }
    },
    
    clearHighlight() {
      this.stopHighlight()
    },
    
    // ========== 4. 折线图 ==========
    initLineChart() {
      const chart = echarts.init(this.$refs.lineChart)
      const color = ['#07D1FA']
      const hexToRgba = (hex, alpha) => {
        const r = parseInt(hex.slice(1, 3), 16)
        const g = parseInt(hex.slice(3, 5), 16)
        const b = parseInt(hex.slice(5, 7), 16)
        return `rgba(${r}, ${g}, ${b}, ${alpha})`
      }
      const option = {
        title: { text: '折线图样式示例', left: 'center', textStyle: { fontSize: 14 } },
        tooltip: { trigger: 'axis' },
        grid: { left: '10%', right: '10%', bottom: '10%', top: '20%' },
        xAxis: {
          type: 'category',
          data: ['周一', '周二', '周三', '周四', '周五', '周六', '周日'],
          boundaryGap: false
        },
        yAxis: { type: 'value', name: '数量' },
        series: [{
          name: '还车数',
          type: 'line',
          symbolSize: 10,
          zlevel: 3,
          lineStyle: {
            color: color[0],
            shadowBlur: 3,
            shadowColor: hexToRgba(color[0], 0.5),
            shadowOffsetY: 8
          },
          itemStyle: {
            color: '#fff',
            borderColor: color[0],
            borderWidth: 2
          },
          symbol: 'circle',
          areaStyle: {
            color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
              { offset: 0, color: hexToRgba(color[0], 0.3) },
              { offset: 1, color: hexToRgba(color[0], 0.1) }
            ])
          },
          data: [120, 132, 101, 134, 90, 230, 210]
        }]
      }
      chart.setOption(option)
      this.charts.line = chart
      this.lineOptionCode = JSON.stringify(option, null, 2).replace(/"function/g, 'function').replace(/}"/g, '}')
    },
    
    // ========== 5. 两柱一线组合图 ==========
    initMixChart() {
      const chart = echarts.init(this.$refs.mixChart)
      const option = {
        title: { text: '两柱一线组合图', left: 'center', textStyle: { fontSize: 14 } },
        tooltip: {
          trigger: 'axis',
          axisPointer: {
            type: 'cross',
            crossStyle: { color: '#999' }
          }
        },
        legend: { data: ['案件数', '结案数', '结案率'], top: 30 },
        grid: { left: '10%', right: '15%', bottom: '10%', top: '20%' },
        xAxis: [{
          type: 'category',
          data: ['00:00', '01:00', '02:00', '03:00', '04:00', '05:00', '06:00', '07:00', '08:00', '09:00', '10:00', '11:00'],
          axisPointer: { type: 'shadow' }
        }],
        yAxis: [
          {
            type: 'value',
            name: '件',
            min: 0,
            max: 250,
            interval: 50
          },
          {
            type: 'value',
            name: '%',
            min: 0,
            max: 100,
            interval: 10,
            position: 'right'
          }
        ],
        series: [
          {
            name: '案件数',
            type: 'bar',
            tooltip: { valueFormatter: value => value + ' 件' },
            data: [2.0, 4.9, 7.0, 23.2, 25.6, 76.7, 135.6, 162.2, 32.6, 20.0, 6.4, 3.3]
          },
          {
            name: '结案数',
            type: 'bar',
            tooltip: { valueFormatter: value => value + ' 件' },
            data: [2.6, 5.9, 9.0, 26.4, 28.7, 70.7, 175.6, 182.2, 48.7, 18.8, 6.0, 2.3]
          },
          {
            name: '结案率',
            type: 'line',
            yAxisIndex: 1,
            tooltip: { valueFormatter: value => value + ' %' },
            data: [2.0, 2.2, 3.3, 4.5, 6.3, 10.2, 20.3, 23.4, 23.0, 16.5, 12.0, 6.2],
            itemStyle: { color: '#fac858' }
          }
        ]
      }
      chart.setOption(option)
      this.charts.mix = chart
      this.mixOptionCode = JSON.stringify(option, null, 2).replace(/"function/g, 'function').replace(/}"/g, '}')
    },
    
    // ========== 6. 一项三柱柱状图 ==========
    initMultiBarChart() {
      const chart = echarts.init(this.$refs.multiBarChart)
      const option = {
        title: { text: '一项三柱柱状图', left: 'center', textStyle: { fontSize: 14 } },
        tooltip: {
          trigger: 'axis',
          axisPointer: { type: 'cross', crossStyle: { color: '#999' } }
        },
        legend: { data: ['案件数', '处置中数量', '已处置数量', '超时数量'], top: 30 },
        dataZoom: [{
          type: 'slider',
          xAxisIndex: [0],
          left: '9%',
          bottom: -5,
          start: 0,
          end: 50
        }],
        grid: { left: '10%', right: '10%', bottom: '15%', top: '20%' },
        xAxis: [{
          type: 'category',
          data: ['清徐县', '阳曲县', '娄烦县', '古交市', '小店区', '迎泽区', '杏花岭区', '尖草坪区', '万柏林区', '晋源区'],
          axisPointer: { type: 'shadow' },
          axisLabel: { interval: 0 }
        }],
        yAxis: [{
          type: 'value',
          name: '件',
          interval: 50,
          axisLabel: { formatter: '{value}' }
        }],
        series: [
          {
            name: '案件数',
            type: 'bar',
            barWidth: 10,
            tooltip: { valueFormatter: value => value + ' 件' },
            data: [2, 4, 7, 23, 25, 76, 135, 162, 32, 20],
            itemStyle: { color: '#5470c6' }
          },
          {
            name: '处置中数量',
            type: 'bar',
            barWidth: 10,
            tooltip: { valueFormatter: value => value + ' 件' },
            data: [3, 5, 8, 26, 28, 70, 175, 182, 48, 18],
            itemStyle: { color: '#91cc75' }
          },
          {
            name: '已处置数量',
            type: 'bar',
            barWidth: 10,
            tooltip: { valueFormatter: value => value + ' 件' },
            data: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10],
            itemStyle: { color: '#fac858' }
          },
          {
            name: '超时数量',
            type: 'bar',
            barWidth: 10,
            tooltip: { valueFormatter: value => value + ' 件' },
            data: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10],
            itemStyle: { color: '#ee6666' }
          }
        ]
      }
      chart.setOption(option)
      this.charts.multiBar = chart
      this.multiBarOptionCode = JSON.stringify(option, null, 2).replace(/"function/g, 'function').replace(/}"/g, '}')
    },
    
    // ========== 工具方法 ==========
    // 切换代码展开/收起
    toggleCode(type) {
      this.showCode[type] = !this.showCode[type]
    },
    
    // 复制代码到剪贴板
    copyCode(code) {
      if (typeof code === 'object') {
        code = JSON.stringify(code, null, 2)
      }
      navigator.clipboard.writeText(code).then(() => {
        this.showToast('复制成功！')
      }).catch(() => {
        // 降级方案
        const textarea = document.createElement('textarea')
        textarea.value = code
        textarea.style.position = 'fixed'
        textarea.style.opacity = '0'
        document.body.appendChild(textarea)
        textarea.select()
        document.execCommand('copy')
        document.body.removeChild(textarea)
        this.showToast('复制成功！')
      })
    },
    
    // 显示提示
    showToast(message) {
      this.toast.message = message
      this.toast.show = true
      setTimeout(() => {
        this.toast.show = false
      }, 2000)
    },
    
    // 窗口大小变化时重新调整图表
    handleResize() {
      Object.values(this.charts).forEach(chart => chart.resize())
    }
  }
}
</script>

<style scoped>
/* ========== 页面整体样式 ========== */
.echarts-demo-page {
  /* 撑满内容区，滚动下沉到模块内部 */
  height: 100%;
  box-sizing: border-box;
  overflow: auto;
  padding: 0;
  background: transparent;
}

.page-title {
  text-align: center;
  color: #303133;
  font-size: 28px;
  margin-bottom: 30px;
  font-weight: 600;
}

/* ========== 卡片样式 ========== */
.chart-card {
  max-width: 1000px;
  margin: 0 auto 30px;
  background: var(--surface-1);
  border-radius: 12px;
  box-shadow: var(--shadow-surface-1);
  overflow: hidden;
  transition: box-shadow 0.3s ease;
}

.chart-card:hover {
  box-shadow: 0 6px 30px rgba(0, 0, 0, 0.12);
}

.card-title {
  font-size: 18px;
  color: #303133;
  padding: 20px 24px 0;
  margin: 0;
  font-weight: 600;
  border-bottom: 1px solid rgba(31, 45, 61, 0.06);
  padding-bottom: 15px;
}

.card-content {
  display: flex;
  padding: 20px 24px;
  gap: 20px;
}

.chart-left {
  flex: 0 0 60%;
  min-height: 350px;
}

.chart-right {
  flex: 0 0 40%;
  background: var(--surface-1);
  border-radius: 8px;
  padding: 16px;
}

.chart-container {
  width: 100%;
  height: 350px;
}

/* ========== 右侧属性列表 ========== */
.section-title {
  font-size: 14px;
  color: #606266;
  margin: 0 0 12px 0;
  font-weight: 600;
}

.prop-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
  max-height: 300px;
  overflow-y: auto;
}

.prop-item {
  background: var(--surface-1);
  border-radius: 6px;
  padding: 10px 12px;
  border-left: 3px solid #409eff;
  position: relative;
}

.prop-name {
  font-size: 13px;
  color: #409eff;
  font-weight: 600;
  background: #ecf5ff;
  padding: 2px 8px;
  border-radius: 4px;
  display: inline-block;
  margin-bottom: 6px;
}

.prop-desc {
  font-size: 12px;
  color: #606266;
  margin: 0 0 8px 0;
  line-height: 1.4;
}

.copy-btn {
  background: #409eff;
  color: #fff;
  border: none;
  padding: 4px 12px;
  border-radius: 4px;
  font-size: 12px;
  cursor: pointer;
  transition: background 0.2s, transform 0.12s cubic-bezier(0.34, 1.4, 0.64, 1);
  will-change: transform;
}

/* 按压反馈：scale(0.96)，不低于 0.95，避免点下去「陷太深」 */
.copy-btn:active {
  transform: scale(0.96);
}

@media (prefers-reduced-motion: reduce) {
  .copy-btn:active {
    transform: none;
  }
}

.copy-btn:hover {
  background: #66b1ff;
}

.copy-btn.full {
  margin-top: 10px;
  padding: 8px 16px;
}

/* ========== 底部按钮区 ========== */
.card-footer {
  padding: 12px 24px;
  background: var(--surface-1);
  border-top: 1px solid #ebeef5;
  display: flex;
  gap: 10px;
}

.toggle-btn, .action-btn {
  padding: 8px 20px;
  border-radius: 6px;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s;
  border: none;
}

.toggle-btn {
  background: #ecf5ff;
  color: #409eff;
  border: 1px solid #d9ecff;
}

.toggle-btn:hover {
  background: #409eff;
  color: #fff;
}

.action-btn {
  background: #f0f9ff;
  color: #13c2c2;
  border: 1px solid #87e8de;
}

.action-btn:hover {
  background: #13c2c2;
  color: #fff;
}

/* ========== 代码展开区 ========== */
.code-panel {
  padding: 20px 24px;
  background: #282c34;
  border-top: 1px solid rgba(31, 45, 61, 0.06);
  animation: slideDown 0.3s ease;
}

@keyframes slideDown {
  from {
    opacity: 0;
    transform: translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.code-block {
  margin: 0;
  padding: 16px;
  background: #1e1e1e;
  border-radius: 8px;
  color: #abb2bf;
  font-family: 'Consolas', 'Monaco', 'Courier New', monospace;
  font-size: 12px;
  line-height: 1.6;
  overflow-x: auto;
  white-space: pre-wrap;
  word-wrap: break-word;
  max-height: 400px;
  overflow-y: auto;
}

/* ========== 提示消息 ========== */
.toast {
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  background: rgba(0, 0, 0, 0.8);
  color: #fff;
  padding: 12px 24px;
  border-radius: 8px;
  font-size: 14px;
  z-index: 9999;
  animation: fadeInOut 2s ease;
}

@keyframes fadeInOut {
  0% { opacity: 0; }
  10% { opacity: 1; }
  90% { opacity: 1; }
  100% { opacity: 0; }
}

/* ========== 响应式适配 ========== */
@media (max-width: 768px) {
  .card-content {
    flex-direction: column;
  }
  
  .chart-left,
  .chart-right {
    flex: 0 0 100%;
  }
  
  .chart-container {
    height: 280px;
  }
}
</style>
